import { useState, useEffect } from 'react';
import { supabase, supabaseAdmin } from '../../config/supabase';
import { Search, Loader2, Edit, Trash2, RefreshCw, Tag, Star, Eye } from 'lucide-react';
import { toast } from '../../components/ui/use-toast';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import EditProfessionalForm, { Professional } from '../../components/admin/EditProfessionalForm';
import { triggerKeywordGeneration } from '../../services/keywordService';
import AdminFeatureManager from '../../components/admin/AdminFeatureManager';
import AdminBusinessPreviewModal from '../../components/admin/AdminBusinessPreviewModal';

interface CategoryInfo {
  id: number;
  name: string;
  category?: string;
}

interface ProfessionalWithRelations extends Professional {
  professionals_categories?: CategoryInfo | null;
}

export default function Professionals() {
  const { adminUser, loading: authLoading } = useAdminAuth();
  const [professionals, setProfessionals] = useState<ProfessionalWithRelations[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProfessional, setSelectedProfessional] = useState<ProfessionalWithRelations | null>(null);
  const [regeneratingKeywords, setRegeneratingKeywords] = useState<Set<number>>(new Set());
  const [featureManagerProfessional, setFeatureManagerProfessional] = useState<ProfessionalWithRelations | null>(null);
  const [previewProfessional, setPreviewProfessional] = useState<ProfessionalWithRelations | null>(null);

  useEffect(() => {
    const loadProfessionals = async () => {
      if (authLoading) return;

      if (!adminUser) {
        setError('Not authorized');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const [{ data: categories, error: categoriesError }, { data: pros, error: prosError }] = await Promise.all([
          supabaseAdmin
            .from('professionals_categories')
            .select('id, name, category')
            .order('name'),
          supabaseAdmin
            .from('professionals')
            .select('*')
            .order('store_name', { ascending: true }),
        ]);

        if (categoriesError) throw categoriesError;
        if (prosError) throw prosError;

        const categoryMap: Record<number, CategoryInfo> = {};
        (categories || []).forEach((c) => {
          categoryMap[c.id] = { id: c.id, name: c.name, category: c.category };
        });

        const combined: ProfessionalWithRelations[] = (pros || []).map((p: any) => ({
          ...p,
          professionals_categories: p.profession_id ? categoryMap[p.profession_id] || null : null,
        }));

        setProfessionals(combined);
      } catch (err) {
        console.error('Error loading professionals:', err);
        setError(err instanceof Error ? err.message : 'Failed to load professionals');
        toast({
          title: 'Error',
          description: 'Failed to load professionals data',
          variant: 'destructive',
        });
        setProfessionals([]);
      } finally {
        setLoading(false);
      }
    };

    loadProfessionals();
  }, [adminUser, authLoading]);

  const handleSearch = (query: string) => {
    setSearchQuery(query.toLowerCase());
  };

  const handleVerify = async (professional: ProfessionalWithRelations) => {
    if (!adminUser) {
      toast({
        title: 'Error',
        description: 'Not authorized to perform this action',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { error: updateError } = await supabase
        .from('professionals')
        .update({ verified: !professional.verified })
        .eq('id', professional.id);

      if (updateError) throw updateError;

      setProfessionals((prev) =>
        prev.map((p) => (p.id === professional.id ? { ...p, verified: !p.verified } : p))
      );

      toast({
        title: 'Success',
        description: `Professional ${professional.verified ? 'unverified' : 'verified'} successfully`,
      });
    } catch (err) {
      console.error('Error updating professional:', err);
      toast({
        title: 'Error',
        description: 'Failed to update professional status',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (professional: ProfessionalWithRelations) => {
    if (!adminUser) {
      toast({
        title: 'Error',
        description: 'Not authorized to perform this action',
        variant: 'destructive',
      });
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${professional.store_name}? This action cannot be undone.`)) {
      return;
    }

    try {
      const { data: existing, error: checkError } = await supabaseAdmin
        .from('professionals')
        .select('id, user_id, email')
        .eq('id', professional.id)
        .single();

      if (checkError) {
        throw checkError;
      }

      if (!existing) {
        setProfessionals((prev) => prev.filter((p) => p.id !== professional.id));
        return;
      }

      const { error: deleteError } = await supabaseAdmin
        .from('professionals')
        .delete()
        .eq('id', professional.id);

      if (deleteError) throw deleteError;

      if (existing.user_id) {
        try {
          const { error: authDeleteError } = await supabaseAdmin.auth.admin.deleteUser(
            existing.user_id
          );

          if (authDeleteError) {
            console.warn('Warning: Failed to delete professional auth user:', authDeleteError);
          }
        } catch (authError) {
          console.warn('Warning: Auth user deletion failed for professional:', authError);
        }
      }

      setProfessionals((prev) => prev.filter((p) => p.id !== professional.id));

      toast({
        title: 'Success',
        description: `Professional "${existing.email}" deleted successfully`,
      });
    } catch (err) {
      console.error(`Deletion error for professional ${professional.id}:`, err);
      toast({
        title: 'Deletion Error',
        description: err instanceof Error ? err.message : 'Failed to delete professional',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (professional: ProfessionalWithRelations) => {
    setSelectedProfessional(professional);
  };

  const handleSave = (updated: Professional) => {
    setProfessionals((prev) =>
      prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p))
    );
    setSelectedProfessional(null);
  };

  const handleClose = () => {
    setSelectedProfessional(null);
  };

  const handleRegenerateKeywords = async (professional: ProfessionalWithRelations) => {
    const idNum = Number(professional.id);
    if (isNaN(idNum)) return;

    setRegeneratingKeywords((prev) => {
      const next = new Set(prev);
      next.add(idNum);
      return next;
    });

    try {
      const success = await triggerKeywordGeneration(idNum, 'professional', {
        store_name: professional.store_name,
        description: professional.description,
        location: professional.location,
      });

      if (success) {
        toast({
          title: 'Success',
          description: `Keyword generation started for ${professional.store_name}`,
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to trigger keyword generation',
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.error('Error triggering keyword generation for professional:', err);
      toast({
        title: 'Error',
        description: 'Failed to trigger keyword generation',
        variant: 'destructive',
      });
    } finally {
      setRegeneratingKeywords((prev) => {
        const next = new Set(prev);
        next.delete(idNum);
        return next;
      });
    }
  };

  const handleToggleMapVisibility = async (professional: ProfessionalWithRelations) => {
    if (!adminUser) {
      toast({
        title: 'Error',
        description: 'Not authorized to perform this action',
        variant: 'destructive',
      });
      return;
    }

    const current = (professional as any).show_map ?? true;
    const next = !current;

    try {
      const { error: updateError } = await supabase
        .from('professionals')
        .update({ show_map: next })
        .eq('id', professional.id);

      if (updateError) throw updateError;

      setProfessionals(prev =>
        prev.map(p =>
          p.id === professional.id
            ? { ...(p as any), show_map: next }
            : p
        )
      );

      toast({
        title: 'Success',
        description: `Map ${next ? 'enabled' : 'disabled'} for this professional`,
      });
    } catch (err) {
      console.error('Error updating professional map visibility:', err);
      toast({
        title: 'Error',
        description: 'Failed to update map visibility',
        variant: 'destructive',
      });
    }
  };

  const filteredProfessionals = professionals.filter((p) => {
    const q = searchQuery.toLowerCase();
    const category = p.professionals_categories;

    const keywordMatch = (p as any).keywords?.some((keyword: string) =>
      keyword.toLowerCase().includes(q)
    ) || false;

    return (
      p.store_name?.toLowerCase().includes(q) ||
      p.owner_name?.toLowerCase().includes(q) ||
      p.email?.toLowerCase().includes(q) ||
      p.location?.toLowerCase().includes(q) ||
      category?.name?.toLowerCase().includes(q) ||
      category?.category?.toLowerCase().includes(q) ||
      keywordMatch
    );
  });

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!adminUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Not authorized to view this page</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Professionals</h1>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search professionals..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProfessionals.map((professional) => (
            <div
              key={professional.id}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
            >
              <div className="p-6">
                <div className="flex items-start space-x-4">
                  <img
                    src={
                      professional.avatar_url ||
                      `https://api.dicebear.com/7.x/initials/svg?seed=${professional.store_name}`
                    }
                    alt={professional.store_name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{professional.store_name}</h3>
                    <p className="text-sm text-gray-600">{professional.owner_name}</p>
                    <p className="text-sm text-gray-500">
                      {professional.professionals_categories?.name || 'No profession'}
                      {professional.professionals_categories?.category &&
                        ` (${professional.professionals_categories.category})`}
                    </p>
                    <p className="text-sm text-gray-500">{professional.location}</p>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
                  <span>Map visibility</span>
                  <button
                    type="button"
                    onClick={() => handleToggleMapVisibility(professional)}
                    className={`px-2 py-1 rounded-full border text-xs font-medium ${
                      ((professional as any).show_map ?? true)
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : 'bg-gray-50 text-gray-500 border-gray-200'
                    }`}
                  >
                    {((professional as any).show_map ?? true) ? 'On' : 'Off'}
                  </button>
                </div>

                {(professional as any).keywords && (professional as any).keywords.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-start gap-2">
                      <Tag className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="flex flex-wrap gap-1">
                        {(professional as any).keywords.slice(0, 5).map((keyword: string, idx: number) => (
                          <span
                            key={idx}
                            className="inline-block px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded-full"
                          >
                            {keyword}
                          </span>
                        ))}
                        {(professional as any).keywords.length > 5 && (
                          <span className="inline-block px-2 py-0.5 text-xs text-gray-500">
                            +{(professional as any).keywords.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex justify-between items-center">
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        professional.verified
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {professional.verified ? 'Verified' : 'Pending'}
                    </span>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setPreviewProfessional(professional)}
                        className="p-2 rounded-full bg-teal-100 hover:bg-teal-200 text-teal-600 transition-colors duration-200"
                        title="Quick Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setFeatureManagerProfessional(professional)}
                        disabled={!professional.user_id}
                        className="p-2 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={professional.user_id ? 'Manage Features' : 'No user_id - cannot manage features'}
                      >
                        <Star className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleRegenerateKeywords(professional)}
                        disabled={regeneratingKeywords.has(Number(professional.id))}
                        className="p-2 rounded-full bg-purple-100 hover:bg-purple-200 text-purple-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Regenerate Keywords"
                      >
                        <RefreshCw
                          className={`w-4 h-4 ${
                            regeneratingKeywords.has(Number(professional.id)) ? 'animate-spin' : ''
                          }`}
                        />
                      </button>
                      <button
                        onClick={() => handleVerify(professional)}
                        className={`p-2 rounded-full transition-colors duration-200 ${
                          professional.verified
                            ? 'bg-yellow-100 hover:bg-yellow-200 text-yellow-600'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                        }`}
                        title={professional.verified ? 'Unverify Professional' : 'Verify Professional'}
                      >
                        {professional.verified ? '✓' : '○'}
                      </button>
                      <button
                        onClick={() => handleEdit(professional)}
                        className="p-2 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 transition-colors duration-200"
                        title="Edit Professional"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(professional)}
                        className="p-2 rounded-full bg-red-100 hover:bg-red-200 text-red-600 transition-colors duration-200"
                        title="Delete Professional"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedProfessional && (
        <EditProfessionalForm
          professional={selectedProfessional as Professional}
          onClose={handleClose}
          onSave={handleSave}
        />
      )}

      {previewProfessional && (
        <AdminBusinessPreviewModal
          businessType="professional"
          business={previewProfessional}
          contextLabel={previewProfessional.professionals_categories?.name}
          onClose={() => setPreviewProfessional(null)}
        />
      )}

      {/* Feature Manager Modal */}
      {featureManagerProfessional && featureManagerProfessional.user_id && (
        <AdminFeatureManager
          userId={featureManagerProfessional.user_id}
          businessName={featureManagerProfessional.store_name}
          onClose={() => setFeatureManagerProfessional(null)}
          onSave={() => {
            toast({
              title: 'Success',
              description: `Features updated for ${featureManagerProfessional.store_name}`,
            });
          }}
        />
      )}
    </div>
  );
}
